import { useState } from 'react'
import { Plus, Search, Edit2, Trash2, Filter, X, ChevronDown, ChevronUp, GitBranch, Copy, List } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { workflowTemplates } from '../data/demoData'
import { getStatusColor, getModuleColor, formatDateShort, generateId } from '../utils/helpers'

const emptyStep = {
  stepId: '', stepNumber: 1, stepName: '', source: 'GPTs', originalLink: '', migrateStatus: '未迁移',
  systemPromptId: '', inputTemplateId: '', outputTemplateId: '', checkerPromptId: '',
  knowledgeIds: [], defaultModel: 'OpenAI', backupModel: '',
  inputRequirements: '', outputStandard: '', needsReview: true, status: '主用', remark: ''
}

const emptyAgent = {
  name: '', module: 'Listing', agentType: 'single', source: 'GPTs', originalLink: '', migrateStatus: '未迁移',
  primaryVersion: '保留链接', owner: '', permission: '全部', defaultModel: 'OpenAI', backupModel: '',
  promptId: '', knowledgeIds: [], outputTemplate: '', status: '主用', remark: '', steps: []
}

const modules = ['Listing', 'Ads', 'Visual', 'QA', '通用']
const sources = ['GPTs', 'Gem', 'Opal', 'exe', 'API Agent', '混合']
const migrateStatuses = ['未迁移', '半迁移', '迁移中', '已API化', '保留链接']
const statuses = ['主用', '测试', '废弃']
const models = ['OpenAI', 'Gemini', '中转API']

export default function AgentManager() {
  const { agents, setAgents, prompts, knowledgeBases, canEdit } = useApp()
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({ module: '', source: '', migrateStatus: '', status: '', agentType: '' })
  const [showModal, setShowModal] = useState(false)
  const [editingAgent, setEditingAgent] = useState(null)
  const [form, setForm] = useState(emptyAgent)
  const [expandedWorkflows, setExpandedWorkflows] = useState({})
  const [activeStepTab, setActiveStepTab] = useState(0)

  const filtered = agents.filter(a => {
    const matchSearch = a.name.toLowerCase().includes(search.toLowerCase()) || a.owner.toLowerCase().includes(search.toLowerCase())
    const matchModule = !filters.module || a.module === filters.module
    const matchSource = !filters.source || a.source === filters.source
    const matchMigrate = !filters.migrateStatus || a.migrateStatus === filters.migrateStatus
    const matchStatus = !filters.status || a.status === filters.status
    const matchType = !filters.agentType || a.agentType === filters.agentType
    return matchSearch && matchModule && matchSource && matchMigrate && matchStatus && matchType
  })

  const openModal = (agent = null) => {
    if (agent) {
      setEditingAgent(agent)
      setForm({ ...agent, steps: agent.steps ? [...agent.steps] : [] })
    } else {
      setEditingAgent(null)
      setForm({ ...emptyAgent, id: generateId(), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() })
    }
    setActiveStepTab(0)
    setShowModal(true)
  }

  const applyTemplate = (tplId) => {
    const tpl = workflowTemplates.find(t => t.id === tplId)
    if (!tpl) return
    const newSteps = tpl.steps.map((s, i) => ({
      ...emptyStep, stepId: generateId(), stepNumber: i + 1, stepName: s.stepName,
      source: s.source, migrateStatus: s.migrateStatus, needsReview: s.needsReview
    }))
    setForm({ ...form, name: tpl.name, module: tpl.module, agentType: 'workflow', steps: newSteps })
  }

  const save = () => {
    if (!form.name) return alert('请输入Agent名称')
    const now = new Date().toISOString()
    if (form.agentType === 'workflow' && (!form.steps || form.steps.length === 0)) return alert('工作流Agent至少需要一个Step')
    if (editingAgent) {
      setAgents(agents.map(a => a.id === editingAgent.id ? { ...form, updatedAt: now } : a))
    } else {
      setAgents([...agents, { ...form, id: generateId(), updatedAt: now }])
    }
    setShowModal(false)
  }

  const remove = (id) => {
    if (!confirm('确定删除该Agent？')) return
    setAgents(agents.filter(a => a.id !== id))
  }

  const toggleExpand = (id) => setExpandedWorkflows({ ...expandedWorkflows, [id]: !expandedWorkflows[id] })

  const addStep = () => {
    const newStep = { ...emptyStep, stepId: generateId(), stepNumber: (form.steps?.length || 0) + 1 }
    setForm({ ...form, steps: [...(form.steps || []), newStep] })
    setActiveStepTab((form.steps?.length || 0))
  }

  const updateStep = (idx, field, val) => {
    const steps = [...form.steps]
    steps[idx] = { ...steps[idx], [field]: val }
    setForm({ ...form, steps })
  }

  const removeStep = (idx) => {
    const steps = form.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepNumber: i + 1 }))
    setForm({ ...form, steps })
    if (activeStepTab >= steps.length) setActiveStepTab(Math.max(0, steps.length - 1))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Agent 工作流中心</h2>
        {canEdit() && (
          <button onClick={() => openModal()} className="btn-primary flex items-center gap-2">
            <Plus size={16} /> 新增 Agent / 工作流
          </button>
        )}
      </div>

      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="input pl-9" placeholder="搜索 Agent 名称或负责人..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="select w-32" value={filters.agentType} onChange={e => setFilters({ ...filters, agentType: e.target.value })}>
            <option value="">全部类型</option>
            <option value="single">单 Agent</option>
            <option value="workflow">工作流</option>
          </select>
          <select className="select w-32" value={filters.module} onChange={e => setFilters({ ...filters, module: e.target.value })}>
            <option value="">全部模块</option>
            {modules.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select className="select w-32" value={filters.migrateStatus} onChange={e => setFilters({ ...filters, migrateStatus: e.target.value })}>
            <option value="">全部状态</option>
            {migrateStatuses.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button onClick={() => { setSearch(''); setFilters({ module: '', source: '', migrateStatus: '', status: '', agentType: '' }) }} className="btn-secondary flex items-center gap-1">
            <X size={14} /> 重置
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {filtered.map(agent => (
          <div key={agent.id} className="card card-hover overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {agent.agentType === 'workflow' && <GitBranch size={18} className="text-purple-600" />}
                {agent.agentType === 'single' && <List size={18} className="text-slate-500" />}
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-slate-800">{agent.name}</h3>
                    <span className={`badge text-[10px] ${agent.agentType === 'workflow' ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-600'}`}>
                      {agent.agentType === 'workflow' ? '工作流' : '单Agent'}
                    </span>
                    <span className={`badge ${getModuleColor(agent.module)}`}>{agent.module}</span>
                    <span className={`badge ${getStatusColor(agent.migrateStatus)}`}>{agent.migrateStatus}</span>
                    <span className={`badge ${getStatusColor(agent.status)}`}>{agent.status}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">
                    {agent.agentType === 'workflow'
                      ? `${agent.steps?.length || 0} 个 Step · `
                      : ''}
                    负责人: {agent.owner} · 模型: {agent.defaultModel} · 更新: {formatDateShort(agent.updatedAt)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {agent.agentType === 'workflow' && (
                  <button onClick={() => toggleExpand(agent.id)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500">
                    {expandedWorkflows[agent.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                )}
                {canEdit() && (
                  <>
                    <button onClick={() => openModal(agent)} className="p-1.5 hover:bg-slate-100 rounded"><Edit2 size={14} className="text-slate-500" /></button>
                    <button onClick={() => remove(agent.id)} className="p-1.5 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-500" /></button>
                  </>
                )}
              </div>
            </div>

            {agent.agentType === 'workflow' && expandedWorkflows[agent.id] && (
              <div className="border-t border-slate-100 bg-slate-50/50">
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-px bg-slate-200 flex-1" />
                    <span className="text-xs text-slate-500">工作流 Step</span>
                    <div className="h-px bg-slate-200 flex-1" />
                  </div>
                  <div className="space-y-2">
                    {agent.steps?.map((step, idx) => (
                      <div key={step.stepId} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                        <div className="w-7 h-7 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center text-xs font-bold">
                          {step.stepNumber}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800">{step.stepName}</div>
                          <div className="text-xs text-slate-500">
                            {step.source} · {step.migrateStatus} · {step.defaultModel}
                            {step.needsReview && <span className="ml-2 text-amber-600">需审核</span>}
                          </div>
                        </div>
                        <span className={`badge text-[10px] ${getStatusColor(step.status)}`}>{step.status}</span>
                      </div>
                    )) || <div className="text-sm text-slate-400 text-center py-2">暂无 Step</div>}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {filtered.length === 0 && <div className="card p-8 text-center text-slate-400">暂无数据</div>}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold">{editingAgent ? '编辑 Agent / 工作流' : '新增 Agent / 工作流'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Agent 名称</label>
                  <input className="input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">类型</label>
                  <select className="select" value={form.agentType} onChange={e => setForm({ ...form, agentType: e.target.value, steps: e.target.value === 'workflow' ? (form.steps || []) : [] })}>
                    <option value="single">单 Agent</option>
                    <option value="workflow">工作流 Agent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">所属模块</label>
                  <select className="select" value={form.module} onChange={e => setForm({ ...form, module: e.target.value })}>
                    {modules.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              {form.agentType === 'workflow' && !editingAgent && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <label className="block text-sm font-medium text-purple-800 mb-2">应用工作流模板</label>
                  <div className="flex gap-2">
                    {workflowTemplates.map(tpl => (
                      <button key={tpl.id} onClick={() => applyTemplate(tpl.id)} className="px-3 py-1.5 bg-white border border-purple-300 text-purple-700 rounded-lg text-sm hover:bg-purple-100">
                        {tpl.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {form.agentType === 'single' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">来源</label>
                    <select className="select" value={form.source} onChange={e => setForm({ ...form, source: e.target.value })}>
                      {sources.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">原始链接</label>
                    <input className="input" value={form.originalLink} onChange={e => setForm({ ...form, originalLink: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">迁移状态</label>
                    <select className="select" value={form.migrateStatus} onChange={e => setForm({ ...form, migrateStatus: e.target.value })}>
                      {migrateStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">绑定 Prompt</label>
                    <select className="select" value={form.promptId || ''} onChange={e => setForm({ ...form, promptId: e.target.value || null })}>
                      <option value="">不绑定</option>
                      {prompts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                </div>
              )}

              {form.agentType === 'workflow' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-slate-800">工作流 Step 配置</h4>
                    <button onClick={addStep} className="btn-primary text-sm flex items-center gap-1 px-3 py-1.5">
                      <Plus size={14} /> 添加 Step
                    </button>
                  </div>
                  {form.steps?.length > 0 && (
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {form.steps.map((step, idx) => (
                        <button key={idx} onClick={() => setActiveStepTab(idx)}
                          className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap ${activeStepTab === idx ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                          Step {step.stepNumber}: {step.stepName || '未命名'}
                        </button>
                      ))}
                    </div>
                  )}
                  {form.steps?.map((step, idx) => activeStepTab === idx && (
                    <div key={idx} className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">Step {step.stepNumber}</span>
                        <button onClick={() => removeStep(idx)} className="text-red-500 text-xs hover:underline">删除 Step</button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <input className="input" placeholder="Step 名称" value={step.stepName} onChange={e => updateStep(idx, 'stepName', e.target.value)} />
                        <select className="select" value={step.source} onChange={e => updateStep(idx, 'source', e.target.value)}>
                          {sources.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select className="select" value={step.migrateStatus} onChange={e => updateStep(idx, 'migrateStatus', e.target.value)}>
                          {migrateStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select className="select" value={step.defaultModel} onChange={e => updateStep(idx, 'defaultModel', e.target.value)}>
                          {models.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <input className="input" placeholder="原始链接" value={step.originalLink} onChange={e => updateStep(idx, 'originalLink', e.target.value)} />
                        <select className="select" value={step.systemPromptId} onChange={e => updateStep(idx, 'systemPromptId', e.target.value)}>
                          <option value="">选择 System Prompt</option>
                          {prompts.filter(p => p.type === 'System Prompt' || p.type === 'Workflow Prompt').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                        <input className="input" placeholder="输入材料要求" value={step.inputRequirements} onChange={e => updateStep(idx, 'inputRequirements', e.target.value)} />
                        <input className="input" placeholder="输出标准" value={step.outputStandard} onChange={e => updateStep(idx, 'outputStandard', e.target.value)} />
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input type="checkbox" checked={step.needsReview} onChange={e => updateStep(idx, 'needsReview', e.target.checked)} /> 需要人工审核
                        </label>
                        <select className="select w-32" value={step.status} onChange={e => updateStep(idx, 'status', e.target.value)}>
                          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <textarea className="textarea h-16 text-sm" placeholder="备注" value={step.remark} onChange={e => updateStep(idx, 'remark', e.target.value)} />
                    </div>
                  ))}
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">负责人</label>
                  <input className="input" value={form.owner} onChange={e => setForm({ ...form, owner: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">默认模型</label>
                  <select className="select" value={form.defaultModel} onChange={e => setForm({ ...form, defaultModel: e.target.value })}>
                    {models.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">状态</label>
                  <select className="select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">备注</label>
                <textarea className="textarea h-20" value={form.remark} onChange={e => setForm({ ...form, remark: e.target.value })} />
              </div>
            </div>
            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="btn-secondary">取消</button>
              <button onClick={save} className="btn-primary">保存</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
